import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@deepguide-ai/ui/dialog';
import { Button } from '@deepguide-ai/ui/button';
import { Input } from '@deepguide-ai/ui/input';
import { Label } from '@deepguide-ai/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@deepguide-ai/ui/select';
import { Alert, AlertDescription } from '@deepguide-ai/ui/alert';
import { RadioGroup, RadioGroupItem } from '@deepguide-ai/ui/radio-group';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { queryClient, trpc } from '@/lib/trpc';
import { ConfigModalProps, IntegrationProvider, ZendeskMetadata } from '../types';
import { ZendeskMetadataSchema } from '@deepguide-ai/types';
import { toast } from 'sonner';
import { APP_NAME } from '@/lib/env';

export const ZendeskConfigModal: React.FC<ConfigModalProps & { onReconnect: IntegrationProvider['handleConnect'] }> = ({
  open,
  onOpenChange,
  onComplete,
  integration,
  workspaceId,
  onReconnect,
}) => {
  // Import settings
  const [importMode, setImportMode] = useState<'all' | 'category'>('all');
  const [importCategoryId, setImportCategoryId] = useState<string>('');
  const [connecting, setConnecting] = useState(false);
  const [page, setPage] = useState({ revision: '', offset: 0 });
  const [webhookId, setWebhookId] = useState('');
  const [webhookError, setWebhookError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const connectionStatus = useQuery({
    ...trpc.integrations.getZendeskConnectionStatus.queryOptions({ integrationId: integration?.id ?? '' }),
    enabled: open && !!integration?.id,
    refetchInterval: open && integration?.id ? 3000 : false,
  });
  const status = connectionStatus.data;
  const sourceRef = status?.sourceRef;
  const collection = status?.ticketCollection;
  const revision = JSON.stringify([integration?.id, sourceRef, collection?.completedAt]);
  const offset = page.revision === revision ? page.offset : 0;
  const canCollect = !!sourceRef && !status?.requiresReconnect && !!status?.features?.includes('tickets') && !connectionStatus.isError;
  const busy = !!status?.syncState && !['completed', 'failed'].includes(status.syncState);
  const ticketOptions = trpc.integrations.getZendeskTickets.queryOptions({ integrationId: integration?.id ?? '', sourceRef: sourceRef ?? '', offset });
  const tickets = useQuery({
    ...ticketOptions,
    // A new successful collection gets a new cache entry, so an older page is
    // never presented as the new result while it is being refreshed.
    queryKey: [...ticketOptions.queryKey, collection?.completedAt ?? null],
    enabled: open && !!integration?.id && canCollect,
  });
  const syncTickets = useMutation(trpc.integrations.syncZendeskTickets.mutationOptions({
    onSuccess: async () => {
      await connectionStatus.refetch();
      toast.success('Zendesk ticket collection queued');
    },
    onError: () => setStartError('Could not queue ticket collection. Check the connection status and try again.'),
  }));

  const bindWebhook = useMutation(trpc.integrations.bindZendeskWebhook.mutationOptions({
    onSuccess: async () => {
      setWebhookId(''); setWebhookError(null);
      await connectionStatus.refetch();
      toast.success('Zendesk webhook settings saved');
    },
    onError: () => setWebhookError('Could not update the webhook. Check the ID, endpoint and administrator access, then retry.'),
  }));

  useEffect(() => {
    setWebhookId(''); setWebhookError(null);
    setPage({ revision: '', offset: 0 });
    setStartError(null);
  }, [open, integration?.id, sourceRef]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const metadata = integration?.metadata as ZendeskMetadata | undefined;
    const zendeskConfig = metadata?.zendeskConfig;

    setImportMode(zendeskConfig?.importMode ?? 'all');
    setImportCategoryId(zendeskConfig?.importCategoryId ?? '');
  }, [integration, open]);

  // Query to get categories from Zendesk
  const categoriesQuery = useQuery({
    ...trpc.integrations.listZendeskCategories.queryOptions({ integrationId: integration?.id ?? '' }),
    enabled: !!integration?.id && open,
  });

  // Test connection mutation
  const testConnectionMutation = useMutation(trpc.integrations.testIntegration.mutationOptions({
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error('Zendesk connection test failed. Reconnect and check account permissions.');
      }
    },
    onError: () => {
      toast.error('Zendesk connection test failed. Reconnect and check account permissions.');
    },
  }));

  // Save configuration mutation
  const saveConfigMutation = useMutation(trpc.integrations.updateIntegration.mutationOptions({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
      toast.success('Configuration saved successfully!');
      onComplete();
      onOpenChange(false);
    },
    onError: () => {
      toast.error('Could not save Zendesk settings. Check your access and try again.');
    },
  }));

  if (!integration) {
    return null;
  }

  const categorySelectionInvalid = importMode === 'category' && (
    categoriesQuery.isPending || categoriesQuery.isError || !categoriesQuery.data?.some(category => String(category.id) === importCategoryId)
  );
  const saveConfiguration = () => {
    if (connecting || saveConfigMutation.isPending || categorySelectionInvalid) return;
    const previousConfig = ZendeskMetadataSchema.shape.zendeskConfig.safeParse(integration.metadata?.zendeskConfig);
    saveConfigMutation.mutate({
      id: integration.id,
      metadata: {
        zendeskConfig: {
          ...(previousConfig.success ? previousConfig.data : {}),
          importMode,
          importCategoryId: importMode === 'category' ? importCategoryId : undefined,
          syncMode: 'import',
        },
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-3xl max-h-[85vh] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Configure Zendesk Integration</DialogTitle>
          <DialogDescription>
            Collect Zendesk tickets and manage how {APP_NAME} imports Help Center content.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1" viewClassName="max-h-[60vh]">
        <div className="space-y-6 px-6 py-4">
          <div className="space-y-3">
            {connectionStatus.isPending && <p role="status" className="text-sm text-muted-foreground">Loading Zendesk connection…</p>}
            {connectionStatus.isError ? (
              <div className="space-y-2">
                <p role="alert" className="text-sm text-destructive">Could not load Zendesk connection status. Retry or reconnect to restore access.</p>
                <Button variant="outline" disabled={connectionStatus.isFetching || connecting}
                  onClick={() => void connectionStatus.refetch()}>Retry connection status</Button>
              </div>
            ) : connectionStatus.data ? (
              <>
                <p className="text-sm">Zendesk account: {connectionStatus.data.providerAccount ? `${connectionStatus.data.providerAccount}.zendesk.com` : 'Unknown'}</p>
                {connectionStatus.data.requiresReconnect && (
                  <p role="status" className="text-sm text-muted-foreground">Reconnect Zendesk to enable staff access collection and ticket history. Your existing Help Center settings are kept.</p>
                )}
                {connectionStatus.data.lastError && (
                  <p role="alert" className="text-sm text-destructive">Zendesk reported a collection or connection problem. Retry collection or reconnect if access has changed. Previously collected data is retained.</p>
                )}
              </>
            ) : null}
            <Button variant="outline" disabled={connecting || saveConfigMutation.isPending || testConnectionMutation.isPending} onClick={async () => {
              if (connecting) return;
              setConnecting(true);
              try {
                await onReconnect({ workspaceId, onSuccess: onComplete, onFinish: () => setConnecting(false) });
              } catch {
                setConnecting(false);
                toast.error('Could not reconnect Zendesk. Try again or ask your administrator to check the app setup.');
              }
            }}>{connecting ? 'Connecting…' : 'Reconnect Zendesk'}</Button>
            <p className="text-sm text-muted-foreground">Reconnect with the same Zendesk account. If you use Help Center publishing, select that permission again during authorization. Rebind ticket webhooks after reconnecting.</p>
          </div>
          <section className="space-y-3 border-t pt-4" aria-labelledby="zendesk-tickets-heading">
            <h3 id="zendesk-tickets-heading" className="text-sm font-medium">Ticket collection</h3>
            <p className="text-sm text-muted-foreground">Collect tickets updated in the last 90 days (since {new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toLocaleDateString()}), across all statuses. Includes API-visible comments and audit events. Attachments and recordings are not downloaded.</p>
            <Button disabled={!canCollect || busy || connecting || syncTickets.isPending} onClick={() => {
              if (!canCollect || busy || connecting || syncTickets.isPending) return;
              setStartError(null);
              syncTickets.mutate({ integrationId: integration.id });
            }}>{syncTickets.isPending ? 'Starting collection…' : busy ? 'Collecting tickets…' : status?.syncState === 'failed' || status?.lastError ? 'Retry ticket collection' : 'Collect tickets'}</Button>
            {busy && <p role="status" className="text-sm text-muted-foreground">Collection is queued or running in the background. You can close this dialog.</p>}
            {status && !status.requiresReconnect && !status.features?.includes('tickets') && <p role="status" className="text-sm text-muted-foreground">Ticket collection is not enabled. Reconnect Zendesk to authorize tickets.</p>}
            {startError && <p role="alert" className="text-sm text-destructive">{startError}</p>}
            {status?.syncState === 'failed' && <p role="alert" className="text-sm text-destructive">Ticket collection failed. Retry collection or check Zendesk access. Previously collected data is retained.</p>}
            {canCollect && <>
              <p role="status" className="text-sm text-muted-foreground">{collection
                ? `Last collected ${new Date(collection.completedAt).toLocaleString()}: ${collection.count} tickets updated since ${new Date(collection.since).toLocaleString()}.`
                : 'No completed ticket collection for this connection.'}</p>
              {collection?.count === 0 && <p className="text-sm text-muted-foreground">The last collection returned no tickets. Retained snapshots below may be older.</p>}
              <h4 className="text-sm font-medium">Collected tickets</h4>
              <p className="text-sm text-muted-foreground">Retained snapshots may include tickets later changed or deleted. History depends on permissions, retention and redactions; a closed ticket does not verify remediation.</p>
              {tickets.isPending && <p role="status" className="text-sm text-muted-foreground">Loading collected tickets…</p>}
              {tickets.isError && <div className="space-y-2">
                <p role="alert" className="text-sm text-destructive">Could not load collected tickets. Previously collected data is retained.</p>
                <Button variant="outline" disabled={tickets.isFetching} onClick={() => void tickets.refetch()}>Retry loading tickets</Button>
              </div>}
              {tickets.isSuccess && tickets.data.tickets.length === 0 && <p className="text-sm text-muted-foreground">No retained tickets on this page.</p>}
              {tickets.data?.tickets.map(ticket => <details key={`${revision}:${ticket.sourceId}`} className="rounded-md border p-3">
                <summary className="cursor-pointer text-sm font-medium">{ticket.title} · Status: {ticket.status}</summary>
                <div className="mt-3 space-y-3 text-sm">
                  <p>Assignee: {ticket.assigneeName ?? 'Unassigned or unavailable'}</p>
                  <p className="text-xs text-muted-foreground">Collected {new Date(ticket.collectedAt).toLocaleString()}</p>
                  {ticket.url?.startsWith(`https://${status.providerAccount}.zendesk.com/agent/tickets/`) && <a className="text-primary underline" href={ticket.url} target="_blank" rel="noopener noreferrer">Open in Zendesk</a>}
                  <p className="whitespace-pre-wrap break-words">{ticket.content}</p>
                </div>
              </details>)}
              <div className="flex gap-2">
                <Button variant="outline" disabled={!offset || tickets.isFetching} onClick={() => setPage({ revision, offset: Math.max(0, offset - 25) })}>Previous</Button>
                <Button variant="outline" disabled={!tickets.data?.hasMore || tickets.isFetching || tickets.isError} onClick={() => setPage({ revision, offset: offset + 25 })}>Next</Button>
              </div>
            </>}
          </section>
          {canCollect && <section className="space-y-3 border-t pt-4" aria-labelledby="zendesk-webhook-heading">
            <h3 id="zendesk-webhook-heading" className="text-sm font-medium">Ticket update webhook</h3>
            <p className="text-sm text-muted-foreground">Optional: refresh individual tickets when Zendesk sends an update. Full ticket collection remains available above.</p>
            <p role="status" className="text-sm">{status?.webhook
              ? `Webhook ${status.webhook.id} bound ${new Date(status.webhook.boundAt).toLocaleString()}.`
              : 'No webhook is bound to this connection.'}</p>
            {status?.webhookUrl && <>
              <Label htmlFor="zendesk-webhook-url">Callback URL</Label>
              <Input id="zendesk-webhook-url" readOnly value={status.webhookUrl} />
              <p className="text-sm text-muted-foreground">In Zendesk Admin Center, create an active JSON POST webhook for triggers and automations using this URL. Add a ticket trigger that sends <code>{'{"ticket_id":"{{ticket.id}}"}'}</code>. Enter the webhook ID below. A Zendesk administrator must authorize the connection so Screenata can retrieve its signing secret.</p>
              <Label htmlFor="zendesk-webhook-id">Zendesk webhook ID</Label>
              <Input id="zendesk-webhook-id" value={webhookId} maxLength={128} disabled={bindWebhook.isPending || connecting}
                onChange={event => setWebhookId(event.target.value)} />
              <div className="flex gap-2">
                <Button variant="outline" disabled={bindWebhook.isPending || connecting || !/^[A-Za-z0-9_-]+$/.test(webhookId)}
                  onClick={() => { setWebhookError(null); bindWebhook.mutate({ integrationId: integration.id, webhookId }); }}>
                  {bindWebhook.isPending ? 'Saving webhook…' : 'Verify and bind webhook'}
                </Button>
                {status.webhook && <Button variant="outline" disabled={bindWebhook.isPending || connecting}
                  onClick={() => { setWebhookError(null); bindWebhook.mutate({ integrationId: integration.id, webhookId: null }); }}>Disable webhook</Button>}
              </div>
            </>}
            {webhookError && <p role="alert" className="text-sm text-destructive">{webhookError}</p>}
            <p className="text-sm text-muted-foreground">Rebind after reconnecting or rotating the Zendesk signing secret. Disabling stops Screenata from accepting updates; it does not delete the Zendesk webhook. Deliveries older than 24 hours are rejected. Use Collect tickets to recover missed updates.</p>
          </section>}
          <div className="space-y-3">
            <Label>Import Mode</Label>
            <RadioGroup value={importMode} disabled={connecting || saveConfigMutation.isPending}
              onValueChange={(value) => { if (value === 'all' || value === 'category') setImportMode(value); }}>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="all" id="import-all" />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="import-all" className="font-normal cursor-pointer">
                    Import All Categories
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Import your entire Zendesk Guide content
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <RadioGroupItem value="category" id="import-category" />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="import-category" className="font-normal cursor-pointer">
                    Import Specific Category
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Choose a specific category to import
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {importMode === 'category' && (
            <div className="space-y-2">
              <Label htmlFor="zendesk-category-select">Select Category to Import</Label>
              <Select
                value={importCategoryId}
                onValueChange={setImportCategoryId}
                disabled={connecting || categoriesQuery.isPending || categoriesQuery.isError || saveConfigMutation.isPending}
              >
                <SelectTrigger id="zendesk-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categoriesQuery.data?.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categoriesQuery.isPending && <p role="status" className="text-sm text-muted-foreground">Loading categories…</p>}
              {categoriesQuery.isError && (
                <div role="alert" className="space-y-2 text-sm text-destructive">
                  <p>Could not load Zendesk categories. Check Help Center access and try again.</p>
                  <Button variant="outline" onClick={() => void categoriesQuery.refetch()} disabled={categoriesQuery.isFetching}>Retry loading</Button>
                </div>
              )}
              {categoriesQuery.isSuccess && categoriesQuery.data.length === 0 && (
                <p role="status" className="text-sm text-muted-foreground">No accessible Help Center categories.</p>
              )}
            </div>
          )}

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Imported articles will be created as new topics in {APP_NAME}. Existing topics will not be overwritten.
            </AlertDescription>
          </Alert>
        </div>
        </ScrollArea>

        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => testConnectionMutation.mutate({ id: integration.id })}
            disabled={connecting || testConnectionMutation.isPending}
          >
            {testConnectionMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              'Test Connection'
            )}
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveConfiguration}
              disabled={connecting || saveConfigMutation.isPending || categorySelectionInvalid}
            >
              {saveConfigMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Save Configuration
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
